import { FC, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import { HoldingClass, Partner } from 'types';
import { TradingOffer } from 'types/TradingOffer';
import { TradingOfferOperation } from 'types/TradingOfferOperation';

import sizes from 'constants/sizes';
import { setModal } from 'modules/_shared/redux/modalActions';
import EditEquityMarketSharePurchaseContractTemplateModal from 'modules/trading/modals/EditEquityMarketSharePurchaseContractTemplate';
import { EditEquityMarketSharePurchaseContractTemplateModalProvider } from 'modules/trading/modals/EditEquityMarketSharePurchaseContractTemplate/context/EditEquityMarketSharePurchaseContractTemplateModalContext';

import EquityMarketAdminPanelOperationsTable from '../../../components/EquityMarketAdminPanelOperationsTable';
import EquityMarketAdminPanelOfferItem from './EquityMarketAdminPanelOfferItem';

/* eslint-disable no-unused-vars -- callback param names in type definitions */
type EquityMarketAdminPanelOffersListProps = {
  societyId: string;
  sellOffers: TradingOffer[];
  buyOffers: TradingOffer[];
  operations: TradingOfferOperation[];
  onViewOfferDetail: (offerId: string) => void;
  onViewOperationDetail: (operationId: string) => void;
  onUpdateOperationStatus: (
    operationId: string,
    status: string,
    comment?: string
  ) => void;
};
/* eslint-enable no-unused-vars */

type TabKey = 'SELL' | 'BUY' | 'OPERATIONS' | 'CONFIG';

const getOfferStatus = (offer: TradingOffer) => {
  const now = new Date();
  const endDate = new Date(offer.endDate);

  if (endDate < now) return 'EXPIRED';
  if (offer.sharesCount.agreed === offer.sharesCount.total) return 'ACCEPTED';
  return 'ACTIVE';
};

const getPartnerName = (partner: string | Partial<Partner>) => {
  if (typeof partner === 'string') return '';
  return partner?.name || '';
};

const getShareClassName = (
  shareClass: string | Partial<HoldingClass> | null
) => {
  if (!shareClass || typeof shareClass === 'string') return '-';
  return shareClass?.name || '-';
};

const EquityMarketAdminPanelOffersList: FC<
  EquityMarketAdminPanelOffersListProps
> = ({
  societyId,
  sellOffers,
  buyOffers,
  operations,
  onViewOfferDetail,
  onViewOperationDetail,
  onUpdateOperationStatus,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState<TabKey>('SELL');

  const handleOpenContractTemplateModal = useCallback(() => {
    dispatch(
      setModal(
        <EditEquityMarketSharePurchaseContractTemplateModalProvider
          societyId={societyId}
          size={sizes.XL}
        >
          <EditEquityMarketSharePurchaseContractTemplateModal />
        </EditEquityMarketSharePurchaseContractTemplateModalProvider>
      )
    );
  }, [dispatch, societyId]);

  return (
    <>
      <ul
        className="nav nav-tabs nav-tabs-s1 nav-panel-admin mb-4 d-flex flex-wrap w-100 align-items-end"
        role="tablist"
      >
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link bg-transparent ${
              activeTab === 'SELL' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('SELL')}
            role="tab"
            aria-selected={activeTab === 'SELL'}
          >
            {t('SellOffers')}
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link bg-transparent ${
              activeTab === 'BUY' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('BUY')}
            role="tab"
            aria-selected={activeTab === 'BUY'}
          >
            {t('BuyOffers')}
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            className={`nav-link bg-transparent ${
              activeTab === 'OPERATIONS' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('OPERATIONS')}
            role="tab"
            aria-selected={activeTab === 'OPERATIONS'}
          >
            {t('Operations')}
          </button>
        </li>
        <li className="nav-item ml-auto pr-0" role="presentation">
          <button
            type="button"
            className={`nav-link bg-transparent ${
              activeTab === 'CONFIG' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('CONFIG')}
            role="tab"
            aria-selected={activeTab === 'CONFIG'}
          >
            <em className="icon ni ni-setting mr-1" />
            {t('EquityMarketAdminConfigurationTab')}
          </button>
        </li>
      </ul>

      <div className="tab-content">
        {/* Tab Ofertas de venta */}
        <div
          className={`tab-pane fade ${
            activeTab === 'SELL' ? 'show active' : ''
          }`}
          role="tabpanel"
        >
          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="card-title mb-3">
                <em className="icon ni ni-wallet-out text-primary mr-2" />
                {t('AllSellOffers')}
              </h6>
              <p className="text-soft fs-13px mb-4">
                {t('OffersTabDescriptionSell')}
              </p>
              {sellOffers.length === 0 ? (
                <div className="text-center py-4">
                  <em className="icon ni ni-inbox fs-3 text-soft d-block mb-2" />
                  <p className="text-soft">{t('NoOffersAvailable')}</p>
                </div>
              ) : (
                <div className="row g-3">
                  {sellOffers.map((offer) => (
                    <EquityMarketAdminPanelOfferItem
                      key={offer._id}
                      offer={offer}
                      isSell
                      onViewDetail={onViewOfferDetail}
                      getOfferStatus={getOfferStatus}
                      getPartnerName={getPartnerName}
                      getShareClassName={getShareClassName}
                      t={t}
                      formatCurrency={formatCurrency}
                      formatNumber={formatNumber}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Ofertas de compra */}
        <div
          className={`tab-pane fade ${
            activeTab === 'BUY' ? 'show active' : ''
          }`}
          role="tabpanel"
        >
          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="card-title mb-3">
                <em className="icon ni ni-wallet-in text-primary mr-2" />
                {t('AllBuyOffers')}
              </h6>
              <p className="text-soft fs-13px mb-4">
                {t('OffersTabDescriptionBuy')}
              </p>
              {buyOffers.length === 0 ? (
                <div className="text-center py-4">
                  <em className="icon ni ni-inbox fs-3 text-soft d-block mb-2" />
                  <p className="text-soft">{t('NoOffersAvailable')}</p>
                </div>
              ) : (
                <div className="row g-3">
                  {buyOffers.map((offer) => (
                    <EquityMarketAdminPanelOfferItem
                      key={offer._id}
                      offer={offer}
                      isSell={false}
                      onViewDetail={onViewOfferDetail}
                      getOfferStatus={getOfferStatus}
                      getPartnerName={getPartnerName}
                      getShareClassName={getShareClassName}
                      t={t}
                      formatCurrency={formatCurrency}
                      formatNumber={formatNumber}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tab Operaciones */}
        <div
          className={`tab-pane fade ${
            activeTab === 'OPERATIONS' ? 'show active' : ''
          }`}
          role="tabpanel"
        >
          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="card-title mb-3">
                <em className="icon ni ni-list text-primary mr-2" />
                {t('AllOperations')}
              </h6>
              <p className="text-soft fs-13px mb-4">
                {t('OperationsTabDescription')}
              </p>
              <EquityMarketAdminPanelOperationsTable
                operations={operations}
                offers={[...sellOffers, ...buyOffers]}
                onViewOperationDetail={onViewOperationDetail}
                onUpdateOperationStatus={onUpdateOperationStatus}
                embedded
              />
            </div>
          </div>
        </div>

        {/* Tab Configuración */}
        <div
          className={`tab-pane fade ${
            activeTab === 'CONFIG' ? 'show active' : ''
          }`}
          role="tabpanel"
        >
          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="card-title mb-1">
                <em className="icon ni ni-file-docs mr-2 text-primary" />
                {t('EquityMarketAdminConfigSection1Title')}
              </h6>
              <p className="text-soft fs-13px mb-4">
                {t('EquityMarketAdminConfigSection1Description')}
              </p>
              <div className="doc-item align-items-start align-items-md-center">
                <div className="doc-icon bg-primary-dim text-primary">
                  <em className="icon ni ni-file-docs" />
                </div>
                <div className="flex-grow-1">
                  <strong className="fs-13px d-block">
                    {t('EquityMarketAdminConfigOfferConditionsTitle')}
                  </strong>
                  <div className="text-soft fs-12px">
                    {t('EquityMarketAdminConfigOfferConditionsSubtitle')}
                  </div>
                </div>
                <div className="d-flex align-items-center flex-wrap flex-shrink-0 mt-3 mt-md-0 ml-md-3">
                  <Link
                    to={`/condiciones-ofertas/${societyId}`}
                    className="btn btn-outline-secondary btn-sm mr-2 mb-2 mb-md-0"
                  >
                    {t('EquityMarketConditionsViewPage')}
                  </Link>
                  <Link
                    to={`/condiciones-ofertas/${societyId}`}
                    className="btn btn-primary btn-sm mb-2 mb-md-0 is-disable"
                  >
                    {t('Edit')}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="card card-bordered mb-4">
            <div className="card-inner">
              <h6 className="card-title mb-1">
                <em className="icon ni ni-file-text mr-2 text-primary" />
                {t('EquityMarketAdminConfigSection2Title')}
              </h6>
              <p className="text-soft fs-13px mb-4">
                {t('EquityMarketAdminConfigSection2Description')}
              </p>
              <div className="doc-item align-items-start align-items-md-center">
                <div className="doc-icon bg-warning-dim text-warning">
                  <em className="icon ni ni-file-text" />
                </div>
                <div className="flex-grow-1">
                  <strong className="fs-13px d-block">
                    {t('EquityMarketAdminConfigContractTitle')}
                  </strong>
                  <div className="text-soft fs-12px">
                    {t('EquityMarketAdminConfigContractSubtitle')}
                  </div>
                </div>
                <div className="flex-shrink-0 mt-3 mt-md-0 ml-md-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleOpenContractTemplateModal}
                  >
                    {t('Edit')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EquityMarketAdminPanelOffersList;
